'use client';
/**
 * A9 — LE PANNEAU DE LA FICHE DE FONCTION.
 *
 * ── Ce qu'il fait, et surtout ce qu'il refuse de faire ─────────────────────
 *
 * Une fiche de fonction porte quatre rubriques. Deux se déduisent de ce que le
 * laboratoire tient déjà à jour — le rattachement est sur l'organigramme, la
 * suppléance sur la cartographie des processus. Deux ne se déduisent d'aucune
 * donnée : à quoi la fonction sert, et ce qu'on y fait.
 *
 * Ce panneau assemble la première moitié et NOMME la seconde. Il ne prétend pas
 * rendre une fiche : il enlève de la table ce qui n'a pas à être ressaisi, pour
 * que la rédaction porte sur ce qui l'exige vraiment.
 *
 * ⚠️ IL N'ÉCRIT RIEN. La fiche est un DOCUMENT de la GED, avec son circuit —
 * rédacteur, vérificateur, approbateur, diffusion. Un panneau qui créerait le
 * document en passant court-circuiterait ce circuit ; la case s'y rattache par
 * `docCode`, en édition de l'organigramme, une fois le document né.
 */
import React from 'react';
import { card, Skeleton, I } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
export interface FicheFonctionData {
  fonction: { id: string; label: string; tier?: string; note?: string; externalise?: boolean };
  rattachement: { id: string; label: string; lien: string } | null;
  subordonnees: { id: string; label: string }[];
  titulaires: { id: string; nom: string; fonction: string }[];
  pilote: { code: string; nom: string; suppleantFonctionLabel: string | null; suppleant: string | null }[];
  supplee: { code: string; nom: string; piloteFonctionLabel: string | null; pilote: string | null }[];
  grilles: { code: string; libelle: string }[];
  nonDeductible: string[];
  /** Le code que la case porte (`FN-TECHNICIEN`) — clé des deux jointures. */
  codeFiche?: string;
  /** Les rubriques RÉDIGÉES, lues sur `org-fonction`. `null` = aucune ligne ne
   *  porte ce code : la fiche est vraiment à écrire. */
  redige?: {
    code: string; libelle: string; amplitude: string;
    /** Celle de la codification de la Qualité (01/09/2026). */
    reference: string;
    /** Celle des classeurs du laboratoire (« ORG.G1.08 »). */
    referencePapier: string;
    version: string; etat: string; dateApplication: string;
    verifiePar: string; approuvePar: string;
    missions: string[]; profil: string[];
    liaisonsHierarchiques: string; liaisonsFonctionnelles: string;
    moyens: string; contraintes: string; conditions: string;
    qualifications: string; engagement: string; reserve: string;
    modifiePar: string; modifieLe: string;
  } | null;
}

const titre: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', margin: '0 0 5px',
};

function Rubrique({ nom, children }: { nom: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 14 }}>
      <h3 style={titre}>{nom}</h3>
      <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>{children}</div>
    </section>
  );
}

const vide = <span style={{ color: 'var(--text-faint)' }}>—</span>;

/** Les trois états du circuit léger. Approuvée n'est PAS « en vigueur » : la
 *  date d'application peut être à venir, et c'est le point de la révision
 *  approuvée d'avance. */
export const ETAT_LABEL: Record<string, string> = {
  brouillon: 'brouillon', averifier: 'vérifiée, en attente d’approbation', approuvee: 'approuvée',
};

/** Une rubrique rédigée : rendue seulement si elle porte quelque chose. Une
 *  liste de titres suivis de tirets ferait lire une fiche vide comme une fiche. */
function Texte({ nom, valeur }: { nom: string; valeur?: string }) {
  if (!valeur || !valeur.trim()) return null;
  return <Rubrique nom={nom}><span style={{ whiteSpace: 'pre-line' }}>{valeur}</span></Rubrique>;
}

function Liste({ nom, items }: { nom: string; items?: string[] }) {
  if (!items || !items.length) return null;
  return (
    <Rubrique nom={nom}>
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {items.map((x, i) => <li key={`${nom}-${i}`}>{x}</li>)}
      </ul>
    </Rubrique>
  );
}

export function PanneauFicheFonction({ id, onFermer }: { id: string; onFermer: () => void }) {
  /* L'état porte l'IDENTIFIANT qu'il décrit. Il remet à zéro par comparaison
     au rendu, et non par un `setState` en tête d'effet : celui-ci relançait un
     rendu à chaque changement de case, et surtout laissait, l'espace d'une
     image, la fiche PRÉCÉDENTE sous le nouveau titre. */
  const [etat, setEtat] = React.useState<{
    id: string; d: FicheFonctionData | null; erreur: string | null;
  }>({ id, d: null, erreur: null });
  const d = etat.id === id ? etat.d : null;
  const erreur = etat.id === id ? etat.erreur : null;

  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/org/fonctions/${encodeURIComponent(id)}/fiche`, { credentials: 'same-origin' })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!vivant) return;
        if (!r.ok || !j?.fiche) {
          setEtat({ id, d: null, erreur: j?.error?.message || 'Fiche indisponible.' });
          return;
        }
        setEtat({ id, d: j.fiche as FicheFonctionData, erreur: null });
      })
      .catch(() => { if (vivant) setEtat({ id, d: null, erreur: 'Fiche indisponible.' }); });
    return () => { vivant = false; };
  }, [id]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Fiche de fonction"
      /* Fenêtre de CONSULTATION : le clic à côté referme, c'est le confort
         que la RQ a demandé de garder le 04/09/2026. Rien ne s'y saisit. */
      onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.34)', zIndex: 90,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
      <div style={{ ...card, width: 'min(680px, 95vw)', maxHeight: '88vh', overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <div>
            <h3 style={titre}>Fiche de fonction</h3>
            <h2 style={{ margin: '2px 0 0', fontSize: 17 }}>{d?.fonction.label || '…'}</h2>
            {d && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                {/* La référence AFFICHÉE, parce qu'une fiche de fonction est une
                    pièce qu'on cite en audit. `reference` est celle que le
                    laboratoire a saisie à la main (« ORG.G1.08 ») ; `codeFiche`
                    est la clé du système. On montre la première quand elle
                    existe, sans jamais inventer la seconde. */}
                {d.redige?.reference || d.redige?.referencePapier || d.codeFiche || 'sans référence'}
                {d.redige && <> · version {d.redige.version} · {ETAT_LABEL[d.redige.etat] || d.redige.etat}</>}
                {d.redige?.reference && d.redige?.referencePapier && (
                  <> · ancienne réf. papier {d.redige.referencePapier}</>
                )}
                {d.redige?.etat === 'approuvee' && d.redige.dateApplication && (
                  <> · applicable le {d.redige.dateApplication}</>
                )}
              </div>
            )}
          </div>
          <Bouton taille="sm" onClick={onFermer}>Fermer</Bouton>
        </div>

        {erreur && <p style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 12 }}>{erreur}</p>}
        {!d && !erreur && <div style={{ marginTop: 14 }}><Skeleton rows={5} /></div>}

        {d && (
          <>
            {/* ⚠️ EN TÊTE, ET NON EN NOTE DE BAS DE PAGE. L'avertissement
                précède ce qu'il qualifie.
                ⚠️ MAIS PLUS INCONDITIONNEL (01/09/2026) : il s'affichait même
                quand les rubriques étaient rédigées, parce que ce panneau ne
                lisait pas la table où elles vivent. « Ce n'est pas encore une
                fiche » servi au-dessus d'une fiche complète apprend à ne plus
                lire les bandeaux. */}
            {!d.redige && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                background: 'var(--amber-50)', border: '1px solid var(--amber-500)',
              }}>
                <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>Ce n’est pas encore une fiche.</strong>
                <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                  {d.nonDeductible.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
            {/* La RÉSERVE d'amorçage : la fiche existe, mais elle a été déduite
                des documents et attend une relecture. C'est un autre état que
                « pas de fiche », et le confondre ferait signer un brouillon. */}
            {d.redige?.reserve && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                background: 'var(--amber-50)', border: '1px solid var(--amber-500)',
              }}>
                <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>Fiche amorcée, non relue.</strong>
                <p style={{ fontSize: 12.5, margin: '5px 0 0', lineHeight: 1.55 }}>{d.redige.reserve}</p>
              </div>
            )}

            <Rubrique nom="Rattachement">
              {d.rattachement ? (
                <>
                  Rattachée à <strong>{d.rattachement.label}</strong>{' '}
                  <span style={{ color: 'var(--text-muted)' }}>
                    (lien {d.rattachement.lien})
                    {/* Le lien fonctionnel n'est pas une nuance de vocabulaire :
                        c'est ainsi que l'indépendance de la RQ est matérialisée
                        sur le document. L'aplatir en « hiérarchique » le
                        contredirait. */}
                    {d.rattachement.lien === 'fonctionnel'
                      ? ' — rattachement fonctionnel, et non hiérarchique'
                      : ''}
                  </span>
                </>
              ) : (
                <>Case racine de l’organigramme : elle ne se rattache à personne.</>
              )}
              {!!d.subordonnees.length && (
                <div style={{ marginTop: 5 }}>
                  Encadre : {d.subordonnees.map((s) => s.label).join(' · ')}
                </div>
              )}
            </Rubrique>

            <Rubrique nom="Suppléance">
              {d.pilote.length ? d.pilote.map((p) => (
                <div key={`p${p.code}`}>
                  Pilote le processus <strong>{p.code} — {p.nom}</strong>
                  {p.suppleantFonctionLabel
                    ? <> ; suppléé par <strong>{p.suppleantFonctionLabel}</strong>{p.suppleant ? ` (${p.suppleant})` : ''}</>
                    : <span style={{ color: 'var(--amber-600)' }}> ; aucune suppléance désignée</span>}
                </div>
              )) : null}
              {d.supplee.length ? d.supplee.map((p) => (
                <div key={`s${p.code}`}>
                  Supplée sur le processus <strong>{p.code} — {p.nom}</strong>
                  {p.piloteFonctionLabel ? <>, piloté par <strong>{p.piloteFonctionLabel}</strong>{p.pilote ? ` (${p.pilote})` : ''}</> : null}
                </div>
              )) : null}
              {!d.pilote.length && !d.supplee.length && vide}
            </Rubrique>

            <Rubrique nom="Qui tient la case aujourd’hui">
              {d.fonction.externalise
                ? <em>Prestataire extérieur — cette case n’a aucun titulaire nominatif.</em>
                : d.titulaires.length
                  ? d.titulaires.map((t) => `${t.nom}${t.fonction ? ` — ${t.fonction}` : ''}`).join(' · ')
                  : <span style={{ color: 'var(--amber-600)', fontWeight: 600 }}>Poste non pourvu.</span>}
            </Rubrique>

            <Rubrique nom="Formation exigée">
              {d.grilles.length
                ? d.grilles.map((g) => `${g.code} — ${g.libelle}`).join(' · ')
                : <span style={{ color: 'var(--text-faint)' }}>
                    Aucune grille rattachée à cette case. Le rattachement est une décision du
                    laboratoire : il n’est pas déduit d’une ressemblance de libellés.
                  </span>}
            </Rubrique>

            {/* ---- CE QUI A ÉTÉ RÉDIGÉ, lu sur `org-fonction` --------------
                C'est la moitié que ce panneau ignorait : elle s'écrit dans
                l'onglet « Fiches de fonction » et ne se lisait que là. */}
            {d.redige ? (
              <>
                <Liste nom="Missions" items={d.redige.missions} />
                <Liste nom="Profil attendu" items={d.redige.profil} />
                <Texte nom="Qualifications exigées" valeur={d.redige.qualifications} />
                <Texte nom="Amplitude horaire" valeur={d.redige.amplitude} />
                <Texte nom="Liaisons hiérarchiques" valeur={d.redige.liaisonsHierarchiques} />
                <Texte nom="Liaisons fonctionnelles" valeur={d.redige.liaisonsFonctionnelles} />
                <Texte nom="Moyens mis à disposition" valeur={d.redige.moyens} />
                <Texte nom="Contraintes" valeur={d.redige.contraintes} />
                <Texte nom="Conditions d’exercice" valeur={d.redige.conditions} />
                <Texte nom="Engagement" valeur={d.redige.engagement} />
                {d.redige.modifieLe && (
                  <p style={{ margin: '14px 0 0', fontSize: 12.5, color: 'var(--text-faint)' }}>
                    Dernière modification le {d.redige.modifieLe}
                    {d.redige.modifiePar ? ` par ${d.redige.modifiePar}` : ''}.
                  </p>
                )}
              </>
            ) : (
              <Rubrique nom="Finalité · Activités">
                <span style={{ color: 'var(--text-faint)' }}>
                  À rédiger, depuis l’onglet <strong>Fiches de fonction</strong> de cet écran. Ce sont
                  les rubriques qu’aucune donnée du système ne porte — et les seules qui demandent
                  votre plume.
                </span>
              </Rubrique>
            )}

            {/* ---- Le lien au système documentaire, dit tel qu'il est -------
                ⚠️ ON N'OUVRE PLUS `docCode` COMME UN DOCUMENT. Aucun document
                ne porte ces codes : le clic quittait l'organigramme pour une
                fiche vide. On dit ce qui existe, et rien de plus. */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-default)' }}>
              {/* ⚠️ CIRCUIT LÉGER, ET LA PHRASE LE DIT (arbitrage RQ, 01/09/2026).
                  Laisser croire à une diffusion ferait attendre des accusés qui
                  ne viendront jamais : la signature du titulaire est papier. */}
              {d.redige ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  <I n="file" s={12} />{' '}
                  {d.redige.etat === 'approuvee' ? (
                    <>
                      Version <strong>{d.redige.version}</strong> approuvée
                      {d.redige.approuvePar ? ` par ${d.redige.approuvePar}` : ''}
                      {d.redige.dateApplication ? `, applicable le ${d.redige.dateApplication}` : ''}.
                      {' '}La modifier ouvrira une révision — une fiche en vigueur ne se corrige pas
                      en place.
                    </>
                  ) : d.redige.etat === 'averifier' ? (
                    <>
                      Version <strong>{d.redige.version}</strong> vérifiée
                      {d.redige.verifiePar ? ` par ${d.redige.verifiePar}` : ''} ; elle attend son
                      approbation par le responsable qualité.
                    </>
                  ) : (
                    <>
                      Version <strong>{d.redige.version}</strong> en <strong>brouillon</strong> : elle
                      n’est pas encore vérifiée, et ne fait donc foi de rien.
                    </>
                  )}
                  <br />
                  La signature du titulaire reste <strong>papier</strong>, dans le dossier
                  d’intégration : cette fiche n’est pas diffusée et n’attend aucun accusé de lecture.
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  <I n="info" s={12} />{' '}
                  Aucune fiche n’est rédigée pour cette case. Elle s’écrit dans l’onglet
                  <strong> Fiches de fonction</strong> de cet écran, puis suit son circuit :
                  rédaction, vérification, approbation.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
